"use client";

import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { sanitizeAnalyticsUrl } from "@/lib/analytics/sanitize-url";

/**
 * PostHog — wrapper client.
 *
 * Tracking inconditionnel (Maroc — pas de bannière de consentement requise) :
 * - Init côté navigateur uniquement, une seule fois.
 * - api_host = /ingest (rewrite 1st-party → eu.i.posthog.com, anti-adblocker).
 * - person_profiles = "identified_only" : profil créé seulement après identify(),
 *   économise le quota MTU PostHog (anonymes = events only).
 * - autocapture activé (clics, forms), capture_pageview manuel (App Router SPA).
 * - session_recording avec maskAllInputs + maskTextSelector pour la santé.
 * - IP blacklisted côté client + sanitize tokens dans les URLs.
 *
 * Skip total : admin.sahha.ma (super-admin pas tracké, données patients).
 */

type Props = {
  children: React.ReactNode;
  /** Si false (admin.sahha.ma), on ne monte pas PostHog. */
  enabled: boolean;
};

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY ?? "";
// Reverse-proxy 1st-party (cf. next.config rewrites). Fallback host direct si var custom fournie.
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_PROXY_PATH ??
  (typeof window !== "undefined" ? `${window.location.origin}/ingest` : "/ingest");
const POSTHOG_UI_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://eu.posthog.com";

let initialized = false;
/**
 * Cette session fait-elle partie du 1 % échantillonné pour le replay ?
 *
 * Mémorisé au chargement parce que la décision est prise UNE fois par session
 * (Math.random au `loaded`). Sans ce drapeau, le redémarrage de
 * l'enregistrement en sortant d'une page sensible réactiverait le replay pour
 * 100 % des sessions — à la fois une régression de confidentialité et le
 * retour du coût recorder.js (156 ms de tâche longue, impact LCP mesuré sur
 * les fiches médecin, audit SEO du 2026-05-07).
 */
let sessionSampledForReplay = false;

export function PostHogProvider({ children, enabled }: Props) {
  useEffect(() => {
    if (!enabled || !POSTHOG_KEY || initialized || typeof window === "undefined") return;

    // Défèrement idle (fix INP 2026-08-17) : l'init PostHog (autocapture +
    // recorder) tournait dès l'hydratation, en compétition avec les
    // premières interactions — INP terrain p75 217-226ms > seuil 200ms,
    // l'origin échouait l'évaluation CWV. requestIdleCallback plafonné à
    // 5s : le main thread reste libre pendant la fenêtre d'interaction
    // initiale, PostHog démarre dès que le navigateur respire. Le pageview
    // initial (que PostHogPageview skippe tant que __loaded est false) est
    // capturé manuellement juste après l'init pour ne perdre aucune session.
    const start = () => {
      if (initialized) return;
      initialized = true;
      initPosthog();
      try {
        const url = new URL(window.location.href);
        ["token", "otp", "code", "secret", "key", "auth"].forEach((p) =>
          url.searchParams.delete(p),
        );
        posthog.capture("$pageview", { $current_url: url.toString() });
      } catch {
        posthog.capture("$pageview");
      }
    };
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(start, { timeout: 5000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = window.setTimeout(start, 3000);
    return () => window.clearTimeout(t);
  }, [enabled]);

  function initPosthog() {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST,
      person_profiles: "identified_only",
      capture_pageview: false, // tracked manually for App Router SPA navigations
      capture_pageleave: true,
      autocapture: true,
      // Deux modules externes chargés à l'init et jamais utilisés par le
      // produit : /ingest/static/surveys.js (35 348 o) et
      // /ingest/static/dead-clicks-autocapture.js (9 618 o), soit 44,9 Ko
      // transférés et 2 requêtes sur chaque page publique — mesurés par
      // PageSpeed sur /dentiste/rabat le 2026-09-21 (mêmes chiffres à ~300 o
      // près sur /generaliste/casablanca et /orl/temara). Aucune enquête n'est
      // définie côté produit : les 31 occurrences de « survey » dans src/ sont
      // toutes éditoriales.
      // dead-clicks pose en plus des écouteurs au niveau document, la famille
      // de coût déjà identifiée ici (cf. report en requestIdleCallback plus
      // haut, « fix INP 2026-08-17 ») — l'INP terrain p75 de l'origine est à
      // 230 ms pour un seuil à 200 ms (CrUX, 69,4 % de « good »).
      // On perd : lancer une enquête PostHog sans redéployer, et l'événement
      // « dead click » dans les tableaux de bord. web-vitals.js (~5 Ko) reste
      // chargé — il alimente les tableaux de bord perf.
      disable_surveys: true,
      capture_dead_clicks: false,
      session_recording: {
        // Masque tous les inputs par défaut → conforme données santé sensibles.
        maskAllInputs: true,
        // Masque tous les textes dans les éléments .ph-mask et data-ph-mask
        maskTextSelector: ".ph-mask, [data-ph-mask], [data-private], input, textarea",
      },
      loaded: (ph) => {
        if (process.env.NODE_ENV !== "production") {
          ph.debug(false); // mettre true pour debug verbose en dev
        }
        // Sampling 1% session replay : recorder.js = 156ms long task observée → impact LCP
        // sur fiches médecin (cf SEO audit 2026-05-07). Décision aléatoire par session.
        // Override possible via Project Settings > Replay sampling sur l'UI PostHog.
        sessionSampledForReplay = Math.random() <= 0.01;
        if (!sessionSampledForReplay) {
          ph.stopSessionRecording();
        }
      },
      // IP blacklistée côté client. Pour anonymiser totalement : Project Settings >
      // Anonymize IPs côté UI PostHog.
      property_blacklist: ["$ip"],
      // Persist via localStorage + cookie (resilient aux blocages 3rd-party cookies).
      persistence: "localStorage+cookie",
      cross_subdomain_cookie: true, // SSO sahha.ma ↔ pro.sahha.ma ↔ app.sahha.ma
      cookie_name: "sahha_ph",
      sanitize_properties: (props) => {
        // Strip query params sensibles (token, otp, returnTo) des URLs trackées.
        // Liste partagée avec GA4 depuis l'audit 2026-09-10 : un seul endroit
        // à durcir, impossible d'en oublier un des deux.
        for (const k of ["$current_url", "$pathname", "$initial_current_url", "$referrer"]) {
          if (typeof props[k] === "string") props[k] = sanitizeAnalyticsUrl(props[k] as string);
        }
        return props;
      },
    });
  }

  if (!enabled || !POSTHOG_KEY) return <>{children}</>;
  return (
    <PHProvider client={posthog}>
      {/* Suspense requis : useSearchParams() bail-out le SSG sinon (Next 15+) */}
      <Suspense fallback={null}>
        <PostHogPageview />
      </Suspense>
      {children}
    </PHProvider>
  );
}

/**
 * Track manuel des pageviews — App Router ne déclenche pas de "full page load" entre routes,
 * il faut donc capturer $pageview manuellement à chaque changement de pathname/search.
 */
/**
 * Surfaces où l'enregistrement de session est INTERDIT (audit 2026-09-10).
 *
 * `maskAllInputs` ne masque que les champs de saisie. Le TEXTE AFFICHÉ ne l'est
 * pas : un dossier de santé, une ordonnance, une liste de patients ou un motif
 * de consultation rendus en lecture partaient intégralement dans le replay
 * PostHog. Sur une plateforme santé, ce sont des données de catégorie spéciale.
 *
 * Le contrôle ne peut PAS vivre dans `[locale]/layout.tsx` : ce layout ne se
 * re-rend pas lors d'une navigation client (next/link), donc un visiteur arrivé
 * par la page d'accueil garderait l'enregistrement actif en entrant dans son
 * dossier. C'est exactement l'incident i18n de juin 2026. D'où ce contrôle ici,
 * dans un composant client qui réagit au pathname.
 */
const SENSITIVE_PATHS = [
  // /mind : questionnaire PHQ-9 / GAD-7 (données de santé) — ni autocapture ni replay (2026-10-06).
  "/mind",
  "/mon-compte",
  "/espace-pro",
  "/teleconsult",
  "/admin",
  "/inscription",
  "/connexion",
  "/mot-de-passe-reinit",
  "/mot-de-passe-oublie",
  // Distances aux laboratoires calculées depuis la position du visiteur : trois
  // distances à des labos aux coordonnées publiques le situent à ~100 m près,
  // alors que la page promet que sa position reste dans son navigateur.
  "/laboratoires/autour-de-moi",
];

/**
 * Retire le préfixe de locale AVANT toute comparaison.
 *
 * Piège maison : `localePrefix: "as-needed"` fait que le français, soit ~87 %
 * du trafic, n'a PAS de préfixe. Un motif écrit sur `/fr/mon-compte` ne
 * matcherait donc jamais en production — échec silencieux, déjà vécu.
 */
function stripLocale(pathname: string): string {
  return pathname.replace(/^\/(fr|ar|en)(?=\/|$)/, "") || "/";
}

function isSensitivePath(pathname: string): boolean {
  const clean = stripLocale(pathname);
  return SENSITIVE_PATHS.some((p) => clean === p || clean.startsWith(p + "/"));
}

function PostHogPageview() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!pathname || typeof window === "undefined") return;
    if (!posthog.__loaded) return;

    // Enregistrement de session : coupé sur les surfaces sensibles, relancé
    // en sortant. `autocapture` est également désactivé quand la version du
    // SDK le permet — il capture le texte des éléments cliqués, donc un nom
    // de patient ou un libellé d'ordonnance.
    const sensitive = isSensitivePath(pathname);
    try {
      if (sensitive) {
        posthog.stopSessionRecording?.();
        posthog.set_config?.({ autocapture: false });
      } else {
        posthog.set_config?.({ autocapture: true });
        // Uniquement si la session avait été retenue par l'échantillonnage à
        // 1 % — sinon on l'activerait pour tout le monde.
        if (sessionSampledForReplay) posthog.startSessionRecording?.();
      }
    } catch {
      // Une API absente selon la version du SDK ne doit jamais casser la page.
    }

    // Le $pageview reste envoyé partout : savoir qu'une page a été vue est une
    // mesure d'audience légitime. C'est le contenu de l'écran qui ne doit pas
    // partir. L'URL est nettoyée de ses paramètres sensibles par
    // `sanitize_properties` (cf. init ci-dessus).
    let url = window.origin + pathname;
    const search = searchParams?.toString();
    if (search) url += `?${search}`;
    posthog.capture("$pageview", { $current_url: url });
  }, [pathname, searchParams]);

  return null;
}
